import { ComponentRef, memo, useRef, useState } from "react";
import isEqual from "react-fast-compare";
import {
	Input,
	// @ts-ignore type
	DrawerBody,
	// @ts-ignore type
	Form,
	Textarea,
	Drawer,
	DrawerContent,
	Checkbox,
	Button,
} from "@heroui/react";
import type { AirplaneInit } from "@trip/verdant";
import { useMemoizedFn, useRequest } from "ahooks";
import showErrMessage from "@/utils/showErrMessage.js";
import { toast } from "sonner";
import { hooks } from "@/store.js";
import AddFormHeader from "@/components/AddFormHeader/index.jsx";
import useDrawerFullPlacement from "@/hooks/useDrawerFullPlacement.js";
import useUpload from "@/hooks/useUpload.js";

const EditForm = memo(
	({
		onClose,
		defaultValue,
		id,
		planId,
	}: {
		onClose: () => void;
		defaultValue?: AirplaneInit;
		id?: string;
		planId: string;
	}) => {
		// const [errors, setErrors] = useState({});
		// const [submitted, setSubmitted] = useState<null | {
		// 	[k: string]: FormDataEntryValue;
		// }>(null);
		const client = hooks.useClient();
		const { runAsync, loading } = useRequest(
			async (payload: { [k: string]: FormDataEntryValue }) => {
				if (!planId) throw new Error("参数不全");
				const seat1Key = "seats.[0].[1]";
				const seat2Key = "seats.[1].[1]";
				const {
					[seat1Key]: seat1,
					[seat2Key]: seat2,
					bagDirect,
					...rest
				} = payload;

				const postObj = {
					planId,
					...rest,
					bagDirect: bagDirect === "true",
					seats: [
						["Y", seat1 as string],
						["S", seat2 as string],
					],
					// cost: payload.cost ? Number(payload.cost) : 0,
				};

				if (!id) {
					if (!defaultValue?.no) throw new Error("参数不全");
					return await client.airplane.put({
						...postObj,
						no: defaultValue?.no,
					});
				}

				const res = await client.airplane.get(id).resolved;
				res?.update(postObj);
			},
			{
				manual: true,
				onError: showErrMessage,
				onSuccess() {
					toast.success("修改成功");
					closeDrawer();
				},
			},
		);

		const onSubmit = useMemoizedFn((e) => {
			e.preventDefault();
			const data = Object.fromEntries(new FormData(e.currentTarget));

			// Custom validation checks
			const newErrors = {};

			// if (Object.keys(newErrors).length > 0) {
			// 	setErrors(newErrors);

			// 	return;
			// }

			// Clear errors and submit
			// setErrors({});
			// setSubmitted(data);

			runAsync(data);
		});

		const [isOpen, setIsOpen] = useState(true);
		const closeDrawer = useMemoizedFn(() => {
			setIsOpen(false);
			setTimeout(() => {
				onClose();
			}, 300);
		});
		const [placement, setPlacement] = useDrawerFullPlacement({ isOpen });
		const itineraryInputRef = useRef<HTMLTextAreaElement>(null);

		return (
			<Drawer
				// @ts-ignore type
				hideCloseButton
				isOpen={isOpen}
				placement={placement}
				onOpenChange={closeDrawer}
			>
				<DrawerContent className="rounded-none max-h-none">
					{(onClose: () => void) => (
						<DrawerBody className="p-0">
							<Form
								className="w-full !block"
								validationBehavior="native"
								// validationErrors={errors}
								// onReset={() => setSubmitted(null)}
								onSubmit={onSubmit}
							>
								<div className=" h-screen pt-safe overflow-scroll px-6 overscroll-contain">
									<AddFormHeader
										onClose={() => {
											setPlacement("bottom");
											onClose();
										}}
										loading={loading}
									/>

									<div className="grid grid-cols-2 mb-3 gap-4">
										<Input
											label="值机台"
											variant="bordered"
											name="counter"
											defaultValue={defaultValue?.counter}
										/>

										<Input
											label="登机口"
											variant="bordered"
											name="gate"
											defaultValue={defaultValue?.gate}
										/>
									</div>

									<div className="grid grid-cols-2 mb-3 gap-4">
										<Input
											label="平台"
											variant="bordered"
											name="platform"
											defaultValue={defaultValue?.platform}
										/>
										<Input
											label="PNR"
											variant="bordered"
											name="pnr"
											defaultValue={defaultValue?.pnr}
										/>
									</div>
									<Input
										label="订单ID"
										variant="bordered"
										name="orderId"
										defaultValue={defaultValue?.orderId}
										className="mb-3"
									/>

									<div className="grid grid-cols-2 mb-3 gap-4">
										<Input
											label="行李额度"
											variant="bordered"
											name="checkedBagKg"
											defaultValue={defaultValue?.checkedBagKg}
										/>
										<Checkbox
											// @ts-ignore type
											label="行李直达"
											variant="bordered"
											name="bagDirect"
											defaultSelected={defaultValue?.bagDirect}
											value="true"
										>
											行李直达
										</Checkbox>
									</div>

									<div className="grid grid-cols-2 mb-3 gap-4">
										<Input
											label="向妤座位"
											variant="bordered"
											name="seats.[0].[1]"
											defaultValue={defaultValue?.seats?.[0]?.[1]}
										/>
										<Input
											label="陈式坚座位"
											variant="bordered"
											name="seats.[1].[1]"
											defaultValue={defaultValue?.seats?.[1]?.[1]}
										/>
									</div>

									<Textarea
										label="行程单"
										variant="bordered"
										name="itinerary"
										defaultValue={defaultValue?.itinerary}
										className="mb-3"
										// isClearable
										ref={itineraryInputRef}
										description={
											<UploadBtn
												onSuccess={({ url }) => {
													if (itineraryInputRef.current) {
														itineraryInputRef.current.value = url;
													}
												}}
											/>
										}
									/>
								</div>
							</Form>
						</DrawerBody>
					)}
				</DrawerContent>
			</Drawer>
		);
	},
	isEqual,
);

const UploadBtn = memo(
	({ onSuccess }: { onSuccess: (payload: { url: string }) => void }) => {
		const inputRef = useRef<HTMLInputElement | null>(null);

		const { handleFileChange, uploadProgress, reset } = useUpload({
			async onSuccess(payload) {
				// STEP: 保存
				toast.success("上传成功");
				onSuccess(payload);
			},
			onFileLoad(reader) {
				// setPreviewImage(reader.target?.result as string);
			},
			keyPrefix: "upload_itinerary",
		});

		return (
			<div className=" relative ">
				<input
					className=" absolute left-0 top-0 w-full h-full opacity-0 z-10"
					type="file"
					onChange={handleFileChange}
					accept="image/*,application/pdf"
					ref={inputRef}
					disabled={uploadProgress > 0}
				/>
				<Button
					className="-ml-1 mt-0.5"
					size="sm"
					variant="faded"
					endContent={<i className="ic-round md-upload -ml-1" />}
					isLoading={uploadProgress > 0}
					spinner={
						// biome-ignore lint/a11y/noSvgWithoutTitle: <explanation>
						<svg
							className="animate-spin h-5 w-5 text-current"
							fill="none"
							viewBox="0 0 24 24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<circle
								className="opacity-25"
								cx="12"
								cy="12"
								r="10"
								stroke="currentColor"
								strokeWidth="4"
							/>
							<path
								className="opacity-75"
								d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								fill="currentColor"
							/>
						</svg>
					}
				>
					点击上传
				</Button>
			</div>
		);
	},
	isEqual,
);
export default EditForm;
